import React, { useRef, useEffect, useState } from "react";
import { StarBorderTwoTone, Star } from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import { useLocation } from "react-router-dom";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { toast } from "react-toastify";
import moment from "moment";
import { TriangleAlert } from "lucide-react";
import ClipLoader from "react-spinners/ClipLoader";
import SyncLoader from "react-spinners/SyncLoader";
import { Avatar, AvatarFallback } from "components/ui/avatar";
import Tooltip from "@mui/material/Tooltip";
import localization from "../../services/localization";
import DocumentMessages from "./DocumentLibrary/DocumentMessages";
import SpecificAPI from "services/SpecificApi";
import { useUserStore } from "../../context/useUserStore";
import { buildFullHtml } from "services/utils";
import { Globe, ArrowUpRight } from "lucide-react";
import { DocumentViewerModal } from "../Components/pdf-viewer/document-viewer-modal";

const ChatMessages = ({
  messages = [],
  botMode,
  updateSavedMessages,
  theme,
  scrollToMessageId,
  setMessages,
  students,
  messagesLoading,
  currentHistory,
  loading,
}) => {
  const [updateLoading, setUpdateLoading] = useState(false);
  const [isOpenDocumentViewer, setIsOpenDocumentViewer] = useState(false);
  const [selectedReference, setSelectedReference] = useState(null);
  const { userData } = useUserStore();
  const copyRef = useRef(null);
  const messageRefs = useRef({});
  const scrollAnchorRef = useRef(null);
  const prevMessagesLengthRef = useRef(messages.length);
  let lastDate = null;

  useEffect(() => {
    if (scrollToMessageId && messageRefs.current[scrollToMessageId]) {
      messageRefs.current[scrollToMessageId].scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [scrollToMessageId]);

  useEffect(() => {
    const prevLength = prevMessagesLengthRef.current;
    const currentLength = messages.length;

    if (currentLength > prevLength) {
      scrollAnchorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }

    // Update ref
    prevMessagesLengthRef.current = currentLength;
  }, [messages]);

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    toast.info("Copied to clipboard! 📋", { position: "top-center" });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";

    const messageDate = moment(timestamp);

    const today = moment();
    const yesterday = moment().subtract(1, "days");

    if (messageDate.isSame(today, "day")) return "Today";
    if (messageDate.isSame(yesterday, "day")) return "Yesterday";

    return messageDate.format("DD/MM/YYYY");
  };

  const formatText = (text) => {
    if (!text) return "";
    return text.replace(/\\n/g, "<br/>");
  };

  const truncateChunk = (text, maxLength = 80) => {
    if (!text) return "";
    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  };

  const handleReferenceClick = (ref) => {
    if (ref.link) {
      window.open(ref.link, "_blank", "noopener,noreferrer");
    } else if (ref.resource_id) {
      setIsOpenDocumentViewer(true);
      setSelectedReference(ref);
    }
  };

  const updateMessages = async (updatedMsg) => {
    try {
      setUpdateLoading(true);
      const res = await SpecificAPI.updateMessages(
        {
          ...updatedMsg,
          _id: updatedMsg.id,
        },
        userData?.selected_school_id,
      );

      setMessages((prev) => prev.map((m) => (m.id === updatedMsg.id ? res.data : m)));
      toast.success(localization.message_updated || "Message updated successfully!");
    } catch (error) {
      console.error("Error updating messages:", error);
      toast.error(localization.error_updating_message || "Error updating message. Please try again.");
    } finally {
      setUpdateLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name || typeof name !== "string") return "U";

    // Split by space or underscore
    const parts = name.split(/[\s_]+/).filter(Boolean);

    if (parts.length === 0) return name.slice(0, 2).toUpperCase();
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  const getAverageRating = (ratingObj) => {
    if (!ratingObj || typeof ratingObj !== "object") return 0;

    const values = Object.values(ratingObj).filter((v) => typeof v === "number");

    if (values.length === 0) return 0;

    return values.reduce((sum, v) => sum + v, 0) / values.length;
  };

  return (
    <div className="chat-messages-container">
      {messagesLoading || loading ? (
        <div className="flex justify-center py-4">
          <ClipLoader size={30} color="var(--primary-icon)" />
        </div>
      ) : (
        messages
          .sort((a, b) => new Date(a.updated_at) - new Date(b.updated_at))
          .map((msg, index) => {
            const msgDate = formatDate(msg.updated_at || msg.created_at);
            const showDateSeparator = msgDate !== lastDate;
            lastDate = msgDate;

            // msg.references = [
            //   {
            //     title: "Regolamento-dIstituto.pdf",
            //     resource_id: "6965022865091c97380cb572",
            //     chunk:
            //       "10 minuti la sorveglianza degli alunni al personale ausiliario;\n oltre i 10 minuti il docente assente sarà sostituito secondo le disposizioni impartite dal docente\n collaboratore/responsabile di plesso.\n\nArt. 27\n\nControllo assenze, ritardi e uscite anticipate degli alunni\n\n1. Il docente della prima ora deve segnalare sul registro di classe e sul r...",
            //     page_number: 13,
            //     position: null,
            //   },
            //   {
            //     title: "Protocollo_di_somministrazione_dei_farmaci_ver-2025.pdf",
            //     resource_id: "696502106c8288de190eb692",
            //     chunk:
            //       "Ministero dell'Istruzione e del Merito\n\nIstituto Comprensivo n. 3\n\nVia Amiterno, 150-66100 Chieti Tel. 0871 574935\n\nCM: CHIC83600E-CF 93048760693\n E mail: chic83600e@istruzione.it\n chic83600e@pec.istruzione.it http:\/\/comprensivo3chieti.edu.it\n\nISTITUTO COMPRENSIVO N.3-CHIETI\n Prot. 0011782 del 11\/09\/2025\n\n(Uscita)\n\neTwinning\n\nSCHOOL\n\nAANGLADE\n ED...",
            //     page_number: 1,
            //     position: null,
            //   },
            // ];

            // Determine type dynamically
            const isUser = !!msg.question;
            const isEditable = msg.is_editable;
            const isHtmlFormat = currentHistory?.prompt?.prompt_template?.html_format === true && !msg.loading;
            const isBot = !!msg.response;

            const isPrompt = !!msg.promptId;

            const user = students.find((u) => u.id === msg.user_id);
            const isStudent = user?.role === "student";
            const lowHealth = msg.response && getAverageRating(msg.prompt_health?.rating) < 2;

            return (
              <React.Fragment key={msg.id || index}>
                {showDateSeparator && (
                  <div className="date-separator">
                    <span>{msgDate}</span>
                  </div>
                )}

                <div
                  ref={(el) => (messageRefs.current[msg.id] = el)} // attach ref
                >
                  {/* USER MESSAGE */}
                  {isUser && (
                    <div className="chat-message user-message">
                      <div className={`user-message-box ${lowHealth ? "has-warning" : ""}`}>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: formatText(msg.question),
                          }}
                        />
                        {lowHealth && (
                          <Tooltip
                            title={
                              localization.message_may_contain_policy_risk || "This message may contain policy risk"
                            }
                            arrow
                          >
                            <TriangleAlert className="message-warning-icon" />
                          </Tooltip>
                        )}
                      </div>
                      {isStudent && (
                        <Tooltip title={user?.name || "Student"} arrow>
                          <Avatar className="h-10 w-10 cursor-pointer">
                            <AvatarFallback className="text-sm">{getInitials(user?.name)}</AvatarFallback>
                          </Avatar>
                        </Tooltip>
                      )}
                    </div>
                  )}

                  {/* BOT MESSAGE */}
                  {(isBot || msg.loading) && (
                    <div className="chat-message bot-message">
                      <div className="bot-message-content">
                        <div className="message-icons">
                          {msg.saved ? (
                            <Star
                              sx={{
                                cursor: "pointer",
                                color: "#ff9800 !important",
                                "&:hover": {
                                  color: theme === "light" ? "#f5f5f5 !important" : "#ececec !important",
                                },
                              }}
                              onClick={() => updateSavedMessages(msg.historyId, msg)}
                            />
                          ) : (
                            <StarBorderTwoTone
                              sx={{
                                cursor: "pointer",
                                color: "#ff9800 !important",
                                "&:hover": {
                                  color: theme === "light" ? "#f5f5f5 !important" : "#ececec !important",
                                },
                              }}
                              onClick={() => updateSavedMessages(msg.historyId, msg)}
                            />
                          )}
                        </div>
                        <div className="message-content chatgpt-markdown">
                          {msg.loading ? (
                            <div className="ai-processing-box">
                              <div className="flex items-center gap-2 text-sm">
                                <SyncLoader size={6} color="#3B82F6" />
                                <span>{localization.loading || "Loading..."}</span>
                              </div>
                            </div>
                          ) : isHtmlFormat ? (
                            <DocumentMessages
                              msg={msg}
                              onUpdate={(updatedResponse) => {
                                const htmlResponse = buildFullHtml(
                                  updatedResponse,
                                  msg.updated_response ?? msg.response,
                                );
                                return updateMessages({
                                  ...msg,
                                  updated_response: htmlResponse,
                                });
                              }}
                              loading={updateLoading}
                            />
                          ) : (
                            <ReactMarkdown
                              components={{
                                code({ node, inline, className, children, ...props }) {
                                  const match = /language-(\w+)/.exec(className || "");
                                  const code = String(children).replace(/\n$/, "");

                                  return !inline && match ? (
                                    <div style={{ position: "relative" }}>
                                      <button
                                        ref={copyRef}
                                        onClick={() => copyToClipboard(code)}
                                        style={{
                                          position: "absolute",
                                          right: "10px",
                                          top: "10px",
                                          background: "#282c34",
                                          color: "#fff",
                                          border: "none",
                                          padding: "5px 10px",
                                          cursor: "pointer",
                                          borderRadius: "4px",
                                        }}
                                      >
                                        {localization.copy || "Copy"}
                                      </button>
                                      <SyntaxHighlighter style={atomDark} language={match[1]} PreTag="div" {...props}>
                                        {code}
                                      </SyntaxHighlighter>
                                    </div>
                                  ) : (
                                    <code className={className} {...props}>
                                      {children}
                                    </code>
                                  );
                                },
                              }}
                            >
                              {msg.response
                                ?.toString()
                                ?.replace(/\\n\\njson/g, "")
                                ?.replace(/\n\njson/g, "")}
                            </ReactMarkdown>
                          )}

                          {/* REFERENCES */}
                          {Array.isArray(msg.references) && msg.references.length > 0 && (
                            <div className="message-references">
                              <Tooltip
                                arrow
                                placement="top-start"
                                componentsProps={{
                                  tooltip: {
                                    className: "beautiful-reference-tooltip",
                                  },
                                }}
                                title={
                                  <div className="reference-card">
                                    {msg.references.map((ref, i) => (
                                      <div
                                        key={i}
                                        onClick={() => handleReferenceClick(ref)}
                                        className="reference-item clickable"
                                      >
                                        <div className="reference-left">
                                          <Globe size={16} className="reference-globe" />

                                          <div className="reference-text">
                                            <span className="reference-title">{ref.title}</span>

                                            {/* 📄 Page number */}
                                            {ref.page_number && (
                                              <span className="reference-meta">Page {ref.page_number}</span>
                                            )}

                                            {/* ✂️ Chunk preview */}
                                            {ref.chunk && (
                                              <span className="reference-chunk">{truncateChunk(ref.chunk)}</span>
                                            )}
                                          </div>
                                        </div>

                                        <ArrowUpRight size={16} className="reference-arrow" />
                                      </div>
                                    ))}
                                  </div>
                                }
                              >
                                <div className="reference-pill">
                                  <Globe size={14} />
                                  <span>Sources</span>
                                  <ArrowUpRight size={12} />
                                </div>
                              </Tooltip>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </React.Fragment>
            );
          })
      )}

      {botMode !== "Free" && (
        <div className="bot-mode-message-content">
          <span className="glow-text">{botMode.toLowerCase()} ...</span>
        </div>
      )}
      <div ref={scrollAnchorRef} />

      {isOpenDocumentViewer && (
        <DocumentViewerModal
          open={isOpenDocumentViewer}
          onOpenChange={setIsOpenDocumentViewer}
          reference={selectedReference}
        />
      )}
    </div>
  );
};

export default ChatMessages;
